import { put, call, takeLatest } from "redux-saga/effects";
import * as api from "./CommentAPI";
import * as actionTypes from "../ActionTypes";
import { actionUpdateGlobalLoaderSagaAction } from "../GlobalLoader/GlobalLoaderAction";

// Create/Save Comment Saga
function* createCommentSaga(action) {
  yield put(actionUpdateGlobalLoaderSagaAction(true));

  try {
    const { apiPayloadRequest, callback } = action.payload || {};


    const response = yield call(api.createComment, apiPayloadRequest);

    yield put({
      type: actionTypes.COMMENT_CREATE_SUCCESS,
      payload: response,
    });

    if (typeof callback === "function") {
      callback({
        type: 1,
        data: response?.data,
        message: response?.message,
        comment: response?.comment,
      });
    }
  } catch (err) {
    const message =
      err?.response?.data?.errors?.[0]?.message ||
      err?.response?.data?.message ||
      err?.message ||
      "Failed to create comment";


    yield put({
      type: actionTypes.COMMENT_CREATE_FAIL,
      payload: message,
    });

    if (typeof action.payload?.callback === "function") {
      action.payload.callback({ type: 0, error: message });
    }
  } finally {
    yield put(actionUpdateGlobalLoaderSagaAction(false));
  }
}

// Update Comment Saga
function* updateCommentSaga(action) {
  yield put(actionUpdateGlobalLoaderSagaAction(true));

  try {
    const { apiPayloadRequest, callback } = action.payload || {};


    const response = yield call(api.updateComment, apiPayloadRequest);

    yield put({
      type: actionTypes.COMMENT_UPDATE_SUCCESS,
      payload: response,
    });

    if (typeof callback === "function") {
      callback({
        type: 1,
        data: response?.data,
        message: response?.message,
        comment: response?.comment,
      });
    }
  } catch (err) {
    const message =
      err?.response?.data?.message ||
      err?.message ||
      "Failed to update comment";


    yield put({
      type: actionTypes.COMMENT_UPDATE_FAIL,
      payload: message,
    });

    if (typeof action.payload?.callback === "function") {
      action.payload.callback({ type: 0, error: message });
    }
  } finally {
    yield put(actionUpdateGlobalLoaderSagaAction(false));
  }
}

// Delete Comment Saga
function* deleteCommentSaga(action) {
  yield put(actionUpdateGlobalLoaderSagaAction(true));

  try {
    const { apiPayloadRequest, callback } = action.payload || {};


    const response = yield call(api.deleteComment, apiPayloadRequest);

    yield put({
      type: actionTypes.COMMENT_DELETE_SUCCESS,
      payload: {
        ...response,
        commentObjId: apiPayloadRequest?.commentObjId,
      },
    });

    if (typeof callback === "function") {
      callback({
        type: 1,
        data: response?.data,
        message: response?.message,
      });
    }
  } catch (err) {
    const message =
      err?.response?.data?.message ||
      err?.message ||
      "Failed to delete comment";


    yield put({
      type: actionTypes.COMMENT_DELETE_FAIL,
      payload: message,
    });

    if (typeof action.payload?.callback === "function") {
      action.payload.callback({ type: 0, error: message });
    }
  } finally {
    yield put(actionUpdateGlobalLoaderSagaAction(false));
  }
}

// Read Comments Saga
function* readCommentsSaga(action) {
  yield put(actionUpdateGlobalLoaderSagaAction(true));

  try {
    const { 
      curriculumObjId, 
      journalObjId, 
      planObjId, 
      commentParentObjId, 
      topLevelOnly,
      page = 1, 
      limit = 10, 
      callback 
    } = action.payload || {};


    const response = yield call(api.readComments, {
      ...(curriculumObjId && { curriculumObjId }),
      ...(journalObjId && { journalObjId }),
      ...(planObjId && { planObjId }),
      ...(commentParentObjId && { commentParentObjId }),
      ...(topLevelOnly !== undefined && { topLevelOnly }),
      page,
      limit,
    });

    yield put({
      type: actionTypes.COMMENT_READ_SUCCESS,
      payload: {
        data: response.data || [],
        pagination: response.pagination || {
          page,
          limit,
          total: 0,
        },
      },
    });

    if (typeof callback === "function") {
      callback({
        type: 1,
        data: response.data,
        pagination: response.pagination,
        message: response?.message || "Comments fetched successfully",
      });
    }
  } catch (err) {
    const message =
      err?.response?.data?.message ||
      err?.message ||
      "Failed to fetch comments";


    yield put({
      type: actionTypes.COMMENT_READ_FAIL,
      payload: message,
    });

    if (typeof action.payload?.callback === "function") {
      action.payload.callback({
        type: 0,
        error: message,
        data: [],
      });
    }
  } finally {
    yield put(actionUpdateGlobalLoaderSagaAction(false));
  }
}

// Get Comment Tree Saga
function* getCommentTreeSaga(action) {
  yield put(actionUpdateGlobalLoaderSagaAction(true));

  try {
    const { apiPayloadRequest, callback } = action.payload || {};


    const response = yield call(api.getCommentTree, apiPayloadRequest);

    yield put({
      type: actionTypes.COMMENT_TREE_SUCCESS,
      payload: response.data || response,
    });

    if (typeof callback === "function") {
      callback({
        type: 1,
        data: response.data || response,
        message: response?.message,
      });
    }
  } catch (err) {
    const message =
      err?.response?.data?.message ||
      err?.message ||
      "Failed to fetch comment tree";


    yield put({
      type: actionTypes.COMMENT_TREE_FAIL,
      payload: message,
    });

    if (typeof action.payload?.callback === "function") {
      action.payload.callback({ type: 0, error: message });
    }
  } finally {
    yield put(actionUpdateGlobalLoaderSagaAction(false));
  }
}

// Save Comment Like Saga
function* saveCommentLikeSaga(action) {
  yield put(actionUpdateGlobalLoaderSagaAction(true));

  try {
    const { apiPayloadRequest, callback } = action.payload || {};


    const response = yield call(api.saveCommentLike, apiPayloadRequest);

    yield put({
      type: actionTypes.COMMENT_LIKE_SAVE_SUCCESS,
      payload: {
        ...response,
        commentObjId: apiPayloadRequest?.commentObjId,
        likeId: response?.likeId || response?.data?._id,
      },
    });

    if (typeof callback === "function") {
      callback({
        type: 1,
        data: response?.data,
        message: response?.message,
      });
    }
  } catch (err) {
    const message =
      err?.response?.data?.message ||
      err?.message ||
      "Failed to like comment";


    yield put({
      type: actionTypes.COMMENT_LIKE_SAVE_FAIL,
      payload: message,
    });

    if (typeof action.payload?.callback === "function") {
      action.payload.callback({ type: 0, error: message });
    }
  } finally {
    yield put(actionUpdateGlobalLoaderSagaAction(false));
  }
}

// Delete Comment Like Saga
function* deleteCommentLikeSaga(action) {
  yield put(actionUpdateGlobalLoaderSagaAction(true));

  try {
    const { apiPayloadRequest, callback } = action.payload || {};


    const response = yield call(api.deleteCommentLike, apiPayloadRequest);

    yield put({
      type: actionTypes.COMMENT_LIKE_DELETE_SUCCESS,
      payload: {
        ...response,
        commentObjId: apiPayloadRequest?.commentObjId,
      },
    });

    if (typeof callback === "function") {
      callback({
        type: 1,
        data: response?.data,
        message: response?.message,
      });
    }
  } catch (err) {
    const message =
      err?.response?.data?.message ||
      err?.message ||
      "Failed to remove like";


    yield put({
      type: actionTypes.COMMENT_LIKE_DELETE_FAIL,
      payload: message,
    });

    if (typeof action.payload?.callback === "function") {
      action.payload.callback({ type: 0, error: message });
    }
  } finally {
    yield put(actionUpdateGlobalLoaderSagaAction(false));
  }
}

// Comment Watcher Saga
export default function* CommentWatcherSaga() {
  yield takeLatest(actionTypes.COMMENT_CREATE_REQUEST, createCommentSaga);
  yield takeLatest(actionTypes.COMMENT_UPDATE_REQUEST, updateCommentSaga);
  yield takeLatest(actionTypes.COMMENT_DELETE_REQUEST, deleteCommentSaga);
  yield takeLatest(actionTypes.COMMENT_READ_REQUEST, readCommentsSaga);
  yield takeLatest(actionTypes.COMMENT_TREE_REQUEST, getCommentTreeSaga);
  yield takeLatest(actionTypes.COMMENT_LIKE_SAVE_REQUEST, saveCommentLikeSaga);
  yield takeLatest(actionTypes.COMMENT_LIKE_DELETE_REQUEST, deleteCommentLikeSaga);
}